import React from 'react';

import cx from 'classnames';

import { useDebounce } from 'components/shared/AssetEditorForm/debounceHook';
import Button from 'components/shared/Button';
import RadioButton from 'components/shared/FormAdapters/FormDefault/RadioButton';
import RadioGroup from 'components/shared/FormAdapters/FormDefault/RadioButton/Group';
import Icon from 'components/shared/Icon';
import { FontAwesomeIcon } from 'components/shared/Icon/FontAwesome';
import Search from 'components/shared/Icon/icons/Search';
import Text from 'components/shared/Typography/Text';
import { SafariPortalIconStyles } from 'constants/fontAwesome';
import { capitalize } from 'utils/common';

import { useIconsSearch } from '../../../../../useIconSearch';
import cs from '../../index.module.css';
import { ICON_FILTER_RADIO_BUTTONS } from './constants';
import { getFilteredIcons } from './functions';
import s from './index.module.css';
import * as t from './types';

const IconSearch = ({ toClose }: t.IconSearchProps) => {
  const [query, debouncedQuery, setQuery] = useDebounce(1000);
  const [selectedIcon, setSelectedIcon] = React.useState<string>('');
  const [iconStyle, setIconStyle] = React.useState<SafariPortalIconStyles>(SafariPortalIconStyles.LIGHT);

  const handleSelectIcon = (isFavorite: boolean) => {
    toClose({ selectedIcon, isFavorite });
  };

  const handleChooseIcon = React.useCallback((icon: string) => setSelectedIcon(icon), []);

  return (
    <>
      <SearchBar value={query} onChange={(e) => setQuery(e.target.value)} />

      <FilterIconRadioGroup iconStyle={iconStyle} setIconStyle={setIconStyle} />

      <SearchResults
        value={debouncedQuery}
        chosenIcon={selectedIcon}
        onChoose={handleChooseIcon}
        iconStyle={iconStyle}
      />
      <div className={cs.footer}>
        <Button
          type="primary2"
          className={cs.buttonText}
          onClick={() => handleSelectIcon(true)}
          disabled={!selectedIcon}
        >
          Select & Make Default
        </Button>
        <Button className={cs.buttonText} onClick={() => handleSelectIcon(false)} disabled={!selectedIcon}>
          Select
        </Button>
      </div>
    </>
  );
};

const SearchBar = ({ value, onChange }: t.SearchBarProps) => {
  return (
    <div className={s.searchBar}>
      <Icon component={Search} />
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder="Type to search..."
        // Prevent Flight search submitting
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.stopPropagation();
          }
        }}
      />
    </div>
  );
};

const SearchResults = ({ value, chosenIcon, onChoose, iconStyle }: t.SearchResultsProps) => {
  const { data = [], isFetching } = useIconsSearch(value);
  const chosenIcons = React.useMemo(() => getFilteredIcons(data)[iconStyle], [iconStyle, data]);

  if (isFetching) {
    return (
      <div className={s.results}>
        {Array.from({ length: 20 }, (_, idx) => (
          <div className={s.option} key={idx}>
            <div className={cx(s.skeleton, s.skeletonIcon)} />
            <div className={cx(s.skeleton, s.skeletonText)} />
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  if (data.length === 0) {
    return <div className={s.noResults}>No results found</div>;
  }

  return (
    <div className={s.results}>
      {chosenIcons.map(({ label, icon, style }) => (
        <SearchResult
          key={icon}
          icon={icon}
          label={`${capitalize(style)} ${label}`}
          isChosen={chosenIcon === icon}
          onChoose={onChoose}
        />
      ))}
    </div>
  );
};

const SearchResult = React.memo<t.SearchResultProps>(({ icon, label, onChoose, isChosen }) => {
  return (
    <div className={cx(s.option, isChosen && s.selected)} onClick={() => onChoose(icon)}>
      <FontAwesomeIcon className={icon} />
      <p>{label}</p>
    </div>
  );
});

const FilterIconRadioGroup = ({ iconStyle, setIconStyle }: t.FilterIconRadioGroupProps) => {
  return (
    <div className={s.radioGroup}>
      <Text type="brandon" isStrong className={s.mediumText}>
        Icon Style Filter:{' '}
      </Text>
      <RadioGroup name="iconStyle" value={iconStyle} onChange={(value) => setIconStyle(value)}>
        {ICON_FILTER_RADIO_BUTTONS.map(({ type, label, circleIcon }) => (
          <RadioButton key={type} value={type} noMargin type="primary">
            <div className={s.radioContentContainer}>
              {circleIcon()}
              <div className={s.radioTitleContainer}>
                <Text type="brandon" size="medium" className={s.mediumText}>
                  {label}
                </Text>
              </div>
            </div>
          </RadioButton>
        ))}
      </RadioGroup>
    </div>
  );
};

export default IconSearch;
